import type { LabRiskSettingsAction } from './actions';
import type { LabRiskSettingsState } from './types';
import { draftFromDefaults, initialLabRiskSettingsState } from './types';

export function labRiskSettingsReducer(
    state: LabRiskSettingsState,
    action: LabRiskSettingsAction,
): LabRiskSettingsState {
    switch (action.type) {
        case 'SET_SCOPE': {
            if (state.firmId === action.firmId && state.superUserMode === action.superUserMode) return state;
            // Toggling superuser mode on the same firm keeps in-progress
            // edits; only a firm change means new data.
            if (state.firmId === action.firmId) return { ...state, superUserMode: action.superUserMode };
            return { ...initialLabRiskSettingsState, firmId: action.firmId, superUserMode: action.superUserMode };
        }
        case 'SET_ACTIVE_TAB':
            return { ...state, activeTab: action.tab };
        case 'SET_DEFAULT_ENABLED':
            return {
                ...state,
                draftDefaults: {
                    ...state.draftDefaults,
                    [action.field]: { ...state.draftDefaults[action.field], enabled: action.enabled },
                },
            };
        case 'SET_DEFAULT_VALUE': {
            // Typing a value opts the row in without a separate checkbox
            // click; clearing it keeps the checkbox as-is so the row
            // doesn't flicker while editing.
            const current = state.draftDefaults[action.field];
            return {
                ...state,
                draftDefaults: {
                    ...state.draftDefaults,
                    [action.field]: {
                        enabled: action.value !== null ? true : current.enabled,
                        value: action.value,
                    },
                },
            };
        }
        case 'SYNC_SAVED_DEFAULTS':
            return {
                ...state,
                savedDefaults: action.defaults,
                draftDefaults: action.resetDraft ? draftFromDefaults(action.defaults) : state.draftDefaults,
            };
        case 'START_SAVE_DEFAULTS':
            if (state.firmId !== action.firmId) return state;
            return { ...state, isSavingDefaults: true };
        case 'APPLY_SAVED_DEFAULTS':
            // A completion for another firm is stale — the scope changed
            // while the request was in flight.
            if (state.firmId !== action.firmId) return state;
            return {
                ...state,
                savedDefaults: action.defaults,
                draftDefaults: draftFromDefaults(action.defaults),
                isSavingDefaults: false,
            };
        case 'FAIL_SAVE_DEFAULTS':
            if (state.firmId !== action.firmId) return state;
            return { ...state, isSavingDefaults: false };
        case 'OPEN_RULE_DIALOG':
            return { ...state, ruleDialog: action.dialog };
        case 'CLOSE_RULE_DIALOG':
            return state.ruleDialog === null ? state : { ...state, ruleDialog: null };
        case 'PATCH_RULE_DRAFT': {
            if (state.ruleDialog === null) return state;
            return {
                ...state,
                ruleDialog: { ...state.ruleDialog, draft: { ...state.ruleDialog.draft, ...action.patch } },
            };
        }
        case 'START_SAVE_RULE': {
            if (state.ruleDialog === null) return state;
            return { ...state, ruleDialog: { ...state.ruleDialog, isSaving: true, error: null } };
        }
        case 'FAIL_SAVE_RULE': {
            if (state.ruleDialog === null) return state;
            return { ...state, ruleDialog: { ...state.ruleDialog, isSaving: false, error: action.message } };
        }
        case 'REQUEST_DELETE_RULE':
            // Ignored while a delete is in flight; the dialog is modal.
            if (state.pendingRuleDeletion?.isDeleting) return state;
            return {
                ...state,
                pendingRuleDeletion: { parameterId: action.parameterId, token: action.token, isDeleting: false },
            };
        case 'CANCEL_DELETE_RULE':
            // Ignored while the request is in flight so the dialog can't
            // vanish out from under it.
            if (state.pendingRuleDeletion === null || state.pendingRuleDeletion.isDeleting) return state;
            return { ...state, pendingRuleDeletion: null };
        case 'START_DELETE_RULE':
            if (state.pendingRuleDeletion === null) return state;
            return { ...state, pendingRuleDeletion: { ...state.pendingRuleDeletion, isDeleting: true } };
        case 'FINISH_DELETE_RULE':
            // A completion for another confirmation (or arriving after a
            // firm switch reset the state) is stale.
            if (state.pendingRuleDeletion?.token !== action.token) return state;
            return { ...state, pendingRuleDeletion: null };
        case 'SET_ADVANCED_FILTER':
            return { ...state, advancedFilter: action.filter };
        case 'SET_ADVANCED_SEARCH_INPUT':
            return { ...state, advancedSearchInput: action.value };

        default: {
            const _exhaustiveCheck: never = action;
            return state;
        }
    }
}
